import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Briefcase, Users, Upload, ArrowRight } from "lucide-react";

export default function LateralDashboard() {
  const navigate = useNavigate();

  const { data: stats } = useQuery({
    queryKey: ["lateral-dashboard-stats"],
    queryFn: async () => {
      const [jobsRes, candidatesRes] = await Promise.all([
        supabase.from("lateral_job_positions").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("lateral_candidates").select("id, status"),
      ]);
      const rows = candidatesRes.data ?? [];
      const applied = rows.filter((c) => c.status === "applied").length;
      const shortlisted = rows.filter((c) => c.status === "shortlisted").length;
      const hired = rows.filter((c) => c.status === "hired").length;
      return {
        activeJobs: jobsRes.count ?? 0,
        totalCandidates: rows.length,
        applied,
        shortlisted,
        hired,
      };
    },
  });

  const tiles = [
    {
      title: "Active Job Positions",
      value: stats?.activeJobs ?? "-",
      icon: Briefcase,
      color: "text-blue-600",
      bg: "bg-blue-50",
      action: () => navigate("/admin/lateral/job-positions"),
    },
    {
      title: "Total Candidates",
      value: stats?.totalCandidates ?? "-",
      icon: Users,
      color: "text-purple-600",
      bg: "bg-purple-50",
      action: () => navigate("/admin/lateral/candidates"),
    },
    {
      title: "New Applications",
      value: stats?.applied ?? "-",
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-50",
      action: () => navigate("/admin/lateral/candidates"),
    },
    {
      title: "Shortlisted",
      value: stats?.shortlisted ?? "-",
      icon: Users,
      color: "text-orange-600",
      bg: "bg-orange-50",
      action: () => navigate("/admin/lateral/candidates"),
    },
    {
      title: "Hired",
      value: stats?.hired ?? "-",
      icon: Users,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      action: () => navigate("/admin/lateral/candidates"),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Lateral Hiring Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Manage experienced candidate pipeline</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Card key={tile.title} className="cursor-pointer hover:shadow-md transition-shadow" onClick={tile.action}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">{tile.title}</CardTitle>
                  <div className={`p-2 rounded-lg ${tile.bg}`}>
                    <Icon className={`h-4 w-4 ${tile.color}`} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{tile.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/lateral/job-positions")}>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="font-semibold">Manage Job Positions</p>
              <p className="text-sm text-gray-500">Create and edit lateral roles</p>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/lateral/candidates")}>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="font-semibold">Candidates</p>
              <p className="text-sm text-gray-500">Add and manage candidates</p>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/lateral/cv-evaluation")}>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="font-semibold">Bulk CV Evaluation</p>
              <p className="text-sm text-gray-500">Screen multiple CVs with AI</p>
            </div>
            <Upload className="h-5 w-5 text-gray-400" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
